import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import FeedbackTable from 'components/FeedbackTable'
import LiveUpdatesDisabledBanner from 'components/LiveUpdatesDisabledBanner'
import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import { useCanOpenLiveUpdates } from 'hooks/useCanOpenLiveUpdates'
import { useLiveUpdatesState } from 'hooks/useLiveUpdatesState'
import { usePermissionResourceLabel } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { Fields as UserProfile } from 'models/UserProfile'
import React from 'react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import Forbidden from '../Forbidden'
import { getViewPath, useOrderFeedback } from './useOrderFeedback'

const t = buildTranslate('users.view.activity.order_feedback')
const liveUpdatesT = buildTranslate('live_updates')

type PropsT = {
  profile: UserProfile
}

// S2 (RecoveryAI Customer Profile Page design spec): the subtitle wraps at
// this width in the prototype, well short of the panel's full content width.
const SUBTITLE_MAX_WIDTH_PX = 560

// S4: the repo has no Lucide dependency, and the one existing chat-bubble
// asset (assets/images/reports/icons/ic-chat.svg) bakes in a fixed
// `fill="#36424E"` on its path rather than `currentColor`, so it can't be
// recoloured to grey40 here without editing an asset shared with Reports.
// This inlines the real Lucide `message-circle` glyph (24x24 viewBox) instead
// — decorative only (aria-hidden), since the copy beneath it says the same
// thing.
const MessageCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
  </svg>
)

// AI-1288 (FR-29/30/31): repoints the Activity tab's "Feedback" panel at
// RecoveryAI order ratings, via the same shared components/FeedbackTable that
// backs the Order feedback inbox (Reports > Order feedback > Details). The
// legacy NPS-shaped Feedback panel this replaces (../Feedback) is left in
// place, unreachable but not deleted — see its locale comment.
const OrderFeedback: React.FC<PropsT> = ({ profile }: PropsT) => {
  const [css] = useStyletron()
  // AI-1291 (FR-61): customer-membership view ('Users') already gates the
  // whole Activity tab one level up (Activity/index.tsx) — this is the
  // narrower ratings-and-reviews gate, same CASL subject/action the Order
  // feedback REPORT page uses for its own Details tab
  // (scenes/Reports/OrderFeedbackReport/index.tsx:107). Reading `ability`
  // directly, not a loading hook, so there's no async gap between "tab
  // mounts" and "gate decided" — nothing renders then disappears.
  const canViewFeedback = useAbility().can('access', 'Feedback')
  const liveUpdatesState = useLiveUpdatesState()
  const canOpenLiveUpdates = useCanOpenLiveUpdates()
  // Body copy, not a tooltip — usePermissionResourceLabel (unlike
  // usePermissionReason) has no dependency on the tooltip-presentation flag,
  // matching the precedent at orderRatings.ts's overview_forbidden block.
  const permissionLabel = usePermissionResourceLabel('Feedback')

  const {
    rows,
    isInitialLoading,
    isLoadingMore,
    isError,
    loadMoreIsError,
    hasMore,
    onLoadMore,
    onRetry,
  } = useOrderFeedback(profile.uid, !canViewFeedback)

  if (!canViewFeedback) {
    return (
      <div>
        <Text.Header4 className="mb-m">{t('title')}</Text.Header4>
        <Forbidden
          title={t('forbidden.title')}
          message={t('forbidden.message', {
            label: permissionLabel ?? t('forbidden.permission_label_fallback'),
          })}
          // This block sits inside one panel, not in place of the whole
          // Activity tab, so it sizes to its content rather than the viewport.
          fullHeight={false}
        />
      </div>
    )
  }

  // S4 (RecoveryAI Customer Profile Page design spec): the empty state
  // replaces the whole table card — no header row — so it only applies once
  // the fetch has actually succeeded with zero rows. Loading and error each
  // keep their own existing state (skeleton rows / FeedbackTable's own alert),
  // neither of which this flag should short-circuit.
  const showEmptyState = !isInitialLoading && !isError && rows.length === 0

  const subtitleClass = css({ maxWidth: `${SUBTITLE_MAX_WIDTH_PX}px` })

  const emptyStateClass = css({
    backgroundColor: merchantTheme.colors.white,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: merchantTheme.colors.grey30,
    borderRadius: '4px',
    paddingTop: '64px',
    paddingBottom: '64px',
    paddingLeft: '24px',
    paddingRight: '24px',
    textAlign: 'center',
  })

  const emptyStateIconClass = css({
    width: '30px',
    height: '30px',
    color: merchantTheme.colors.grey40,
    display: 'block',
    marginTop: 0,
    marginRight: 'auto',
    marginBottom: '16px',
    marginLeft: 'auto',
  })

  return (
    <div>
      <div className="mb-m">
        <Text.Header4>{t('title')}</Text.Header4>
        {/* S2, verbatim copy — max-width matches the prototype's wrap point. */}
        <Text.BodyText4
          color="grey70"
          className={`line-height-20 mt-xxs ${subtitleClass}`}
        >
          {t('subtitle')}
        </Text.BodyText4>
      </div>
      {liveUpdatesState.isDisabled && (
        <div className="mb-m" data-testid="customer-profile-disabled-banner">
          <LiveUpdatesDisabledBanner
            body={liveUpdatesT('disabled_banner.customer_profile.body')}
            resumeText={liveUpdatesT('disabled_banner.customer_profile.resume')}
            resume={
              canOpenLiveUpdates
                ? { kind: 'link', to: LIVE_UPDATES_PATH }
                : undefined
            }
            compact
            headingTag="h5"
          />
        </div>
      )}
      {showEmptyState ? (
        // S4: replaces the table card entirely — no header row — per the
        // ruling on this ticket (components/FeedbackTable stays untouched;
        // it's shared with the Order feedback inbox Details tab and RAI
        // Escalations, neither of which asked for this icon).
        <div className={emptyStateClass} data-testid="order-feedback-empty">
          <MessageCircleIcon className={emptyStateIconClass} />
          <Text.BodyText3 color="grey70">{t('empty_state')}</Text.BodyText3>
        </div>
      ) : (
        <FeedbackTable
          rows={rows}
          isLoading={isInitialLoading}
          isError={isError}
          onRetry={onRetry}
          // The backend contract for GET /users/:identifier/order_ratings takes
          // no sort param (page/per_page only) — it always returns most-recent
          // first, matching FR-30's "most recent 10 items by default". The Date
          // header's sort control isn't optional on the shared table, but
          // there's nothing server-side for it to toggle, so direction is fixed
          // and a click is a no-op rather than a fake local-only re-sort.
          sortDirection="desc"
          onSortChange={() => {}}
          getViewPath={getViewPath}
          // FR-30: filtered to this customer already (the identifier scopes the
          // whole query), so the Customer column is redundant — every row is
          // this same customer.
          showCustomerColumn={false}
          // This customer's free-text comment is the point of the tab; the
          // Details tab opts into the same column for the same reason.
          showCommentColumn
          // FR-30 keeps Location, unlike the Details tab (which hides it).
          showLocationColumn
          // Both other surfaces of this table opt in (the Details tab and RAI
          // Escalations, per the prop's own comment), so a third surface
          // rendering the legacy solid pill instead would make the same rating
          // look different depending on where you opened it.
          subtleResolvedBadge
          // Kept even though showEmptyState now intercepts the zero-row case
          // above — FeedbackTable's contract still requires these, and a
          // sibling surface's own filter (never present here) or a future
          // regression in showEmptyState should still fail into readable
          // copy, not a blank table.
          emptyMessage={t('empty_state')}
          // No filter UI on this surface (unlike the Order feedback inbox), so
          // this branch is unreachable — hasActiveFilters is always false.
          filteredEmptyMessage={t('filtered_empty_state')}
          errorMessage={t('error')}
          hasActiveFilters={false}
        />
      )}
      {!isError && hasMore && (
        <div className="d-flex flex-column align-items-center mt-m">
          <Button
            kind="secondary"
            onClick={onLoadMore}
            isLoading={isLoadingMore}
            disabled={isLoadingMore}
          >
            {t('load_more')}
          </Button>
          {/* A failed "Load more" keeps every already-loaded row on screen
              (see useOrderFeedback's loadMoreIsError doc comment) — this is
              the ONLY thing that changes, a small inline notice next to the
              retry-by-reclicking button above, not FeedbackTable's full error
              state. */}
          {loadMoreIsError && (
            <Text.BodyText4 color="cayenne50" className="mt-xs">
              {t('error')}
            </Text.BodyText4>
          )}
        </div>
      )}
    </div>
  )
}

export default OrderFeedback
